import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../hooks/useStore';
import { ContentHead, Loading } from '../../lib/components';
import { FeedbackAlert } from '../../lib/components/atomic/feedback/alert';
import { ContentBox } from '../../lib/components/compound/pages/contentBox';
import { SettingsFace, SetupFace } from '../../lib/components/compound/pxtraCardOnboarding';
import { useCardOnboarding } from '../../queries/pxtraCard';

/**
 * One route, two faces: the setup checklist while the Swan check runs, the
 * pxtra Card settings once it is done. The entry never becomes two.
 */
export const CardSettings = observer(() => {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const companyId = companyStore.company?.id ?? '';

  const { data: onboarding, isLoading, isError } = useCardOnboarding(companyId);

  if (isLoading) {
    return (
      <ContentBox>
        <Loading />
      </ContentBox>
    );
  }

  // A failed read leaves no data behind, and spinning forever would claim the
  // page is still loading when nothing else is coming.
  if (isError || !onboarding) {
    return (
      <ContentBox>
        <FeedbackAlert
          severity="error"
          bulletPoints={false}
          title={t('pxtraCard:onboardingUnavailableTitle')}
          description={t('pxtraCard:onboardingUnavailableText')}
        />
      </ContentBox>
    );
  }

  const isVerified = onboarding.phase === 'verified';

  const face = () => {
    if (onboarding.phase === 'refused') {
      return (
        <FeedbackAlert
          severity="error"
          bulletPoints={false}
          title={t('pxtraCard:refusedTitle')}
          description={`${onboarding.refused_reason} ${t('pxtraCard:refusedHint')}`.trim()}
        />
      );
    }

    if (isVerified) return <SettingsFace companyId={companyId} onboarding={onboarding} />;

    return <SetupFace companyId={companyId} onboarding={onboarding} />;
  };

  return (
    <ContentBox
      headComponent={
        <ContentHead
          headline={t(isVerified ? 'pxtraCard:settingsHeadline' : 'pxtraCard:setupHeadline')}
          subtitle={t(isVerified ? 'pxtraCard:settingsSubtitle' : 'pxtraCard:setupSubtitle')}
          showActions={false}
        />
      }
    >
      {face()}
    </ContentBox>
  );
});
